import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { ervenyesJogkorNezet } from './jogkor-nezet-tarolo';

/**
 * A backend felé menő /api hívásokra ráteszi a Keycloak access tokent
 * (Authorization: Bearer ...). Az aktív auth lib az angular-oauth2-oidc,
 * ezért az OAuthService.getAccessToken()-t használjuk.
 *
 * FONTOS: az ApiPrefixInterceptor ELŐTT kell futnia (providers sorrend),
 * hogy még a relatív "/api" URL-t lássa (natívon az ApiPrefix abszolúttá írja).
 *
 * Kihagyás:
 *  - "Skip-Auth" fejléces kérések (szándékos kihagyás),
 *  - nem /api hívások (külső API-k, pl. mérföldkövek),
 *  - már Authorization-t hordozó kérések (pl. POS terminál saját tokenje).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  MIÉRT VÁRUNK A TOKENRE?
 *
 *  A `getAccessToken()` akkor is visszaad egy tokent, ha az MÁR LEJÁRT, és üresen
 *  tér vissza akkor is, ha a munkamenet ÉPP ÉPÜL (a webes csendes frissítés fut, vagy
 *  most jöttünk vissza a bejelentkezésből). Mindkét esetben elment a kérés – az egyik
 *  lejárt jeggyel, a másik jegy nélkül –, a kiszolgáló pedig nem tudta azonosítani a
 *  felhasználót. A tünet félrevezető volt: „nem tud azonosítani", pedig be volt lépve,
 *  csak épp egy fél másodperccel korábban indult a kérés, mint ahogy a jegy megjött.
 *  Tipikusan az ELSŐ betöltésnél jött elő, miután a Keycloak kiléptetett és a
 *  felhasználó újra belépett.
 *
 *  Ezért ha nincs ÉRVÉNYES token, de látszik, hogy készül egy munkamenet (van
 *  frissítő jegy, vagy van egy lejárt token, amit a könyvtár épp cserél), akkor a
 *  kérést rövid ideig visszatartjuk. Ez NEM végtelen várakozás: a határidő letelte
 *  után a kérés ugyanúgy elmegy, ahogy eddig – a legrosszabb eset változatlan.
 *
 *  És ha egyszer letelt a határidő (tehát tényleg nincs munkamenet), egy ideig NEM
 *  várakozunk újra: különben egy kiléptetett felhasználónál MINDEN kérés belefutna a
 *  teljes várakozásba, és az egész felület dermedtnek tűnne.
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Meddig várunk legfeljebb egy épülő munkamenetre. */
const VARAKOZAS_MS = 4000;
/** Ennyi ideig nem próbálkozunk újra, ha az előző várakozás eredménytelen volt. */
const HIABA_VART_SZUNET_MS = 10000;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /** Mikor telt le utoljára eredménytelenül a várakozás. */
  private hiabaVart = 0;

  constructor(private oauth: OAuthService, private auth: AuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.headers.has('Skip-Auth')) {
      return next.handle(req.clone({ headers: req.headers.delete('Skip-Auth') }));
    }

    const isApi = req.url.startsWith('/api') || req.url.startsWith('api/') || this.sajatBackendAbszolut(req.url);
    if (!isApi || req.headers.has('Authorization')) {
      return next.handle(req);
    }

    /*  NÉZETVÁLTÁS: a menet azonosítója MINDEN kéréssel megy.
     *
     *  Eddig a „belépés más nevében" csak a böngészőben történt meg: a felület átírta
     *  a kilétet, a kérésekhez viszont változatlanul az ADMIN tokenje ment, tehát a
     *  kiszolgáló végig adminnak látta a kérőt – így a sima dolgozó nevében is nyitva
     *  maradtak az admin-nézetek és az admin-adatok. A menet azonosítójából a
     *  kiszolgáló tudja, kinek a nevében kell futnia a kérésnek.
     *
     *  A menet-azonosító önmagában nem jogosít: a kiszolgáló ellenőrzi, hogy a menet
     *  nyitva van-e, és hogy épp AZ indította-e, akinek a tokenje most jött. */
    req = this.nezetvaltasFejlec(req);
    req = this.jogkorNezetFejlec(req);

    // Standalone, jelszavas módban a helyi tokent küldjük; ott nincs mire várni.
    if (this.auth.isStandaloneLogin()) {
      const helyi = this.auth.getLocalToken();
      return next.handle(helyi
        ? req.clone({ setHeaders: { Authorization: 'Bearer ' + helyi } })
        : req);
    }

    const kuld = (token: string | null) => next.handle(token ? req.clone({ setHeaders: { Authorization: 'Bearer ' + token } }) : req);
    return from(this.tokentVar()).pipe(switchMap((token) => kuld(token).pipe(
      /*  401 = a jegy a kiszolgáló szerint lejárt (pl. ébredés után, vagy eltérő óra miatt): a tartós
       *  (offline) jeggyel EGYSZER frissítünk, és a kérést újraküldjük. Ha a frissítés nem sikerül, az
       *  AuthService a belépésre visz – a hiba pedig változatlanul megy tovább. */
      catchError((e: any) => {
        if (!(e instanceof HttpErrorResponse) || e.status !== 401 || !this.oauth.getRefreshToken()) return throwError(() => e);
        return from(this.auth.tartosFrissit()).pipe(switchMap((ok) => (ok ? kuld(this.oauth.getAccessToken()) : throwError(() => e))));
      }),
    )));
  }

  /*  ABSZOLÚT CÍMMEL HÍVOTT SAJÁT BACKEND.
   *
   *  Néhány képernyő (értékelések, kerettanterv-sorrend, dokumentumok) a backend teljes
   *  címét rakja össze (`https://erp.<domain>/api/...`). Ezek eddig token nélkül mentek ki,
   *  mert csak a relatív `/api` számított saját hívásnak – a kiszolgáló így nem tudta, ki
   *  kérdez, és például a jegymódosítást az Értékelés képernyőről 403-mal elutasította.
   *  Idegen címre továbbra sem megy token: csak a saját backend címére. */
  private sajatBackendAbszolut(url: string): boolean {
    if (!/^https?:\/\//i.test(url)) return false;
    const origin = String(environment.apiOrigin || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
    const jelolt = [origin, typeof window !== 'undefined' ? window.location.origin : '']
      .filter((o) => /^https?:\/\//i.test(o));
    return jelolt.some((o) => url === `${o}/api` || url.startsWith(`${o}/api/`));
  }

  /** A nyitott nézetváltás menet-azonosítója a kérés fejlécébe. */
  private nezetvaltasFejlec(req: HttpRequest<any>): HttpRequest<any> {
    try {
      const nyers = localStorage.getItem('cronom_impersonalas');
      const imp = nyers ? JSON.parse(nyers) : null;
      const menet = imp?.menet ? String(imp.menet) : '';
      /*  A nézetváltás SAJÁT végpontjait kihagyjuk: a lezárást és az állapot-lekérdezést
       *  a VALÓDI kilétnek kell végeznie, különben a cél nevében próbálnánk lezárni a
       *  menetet – és nem tudnánk kilépni belőle. */
      if (!menet || /\/api\/impersonalas(\/|$)/.test(req.url)) return req;
      return req.clone({ setHeaders: { 'X-Impersonalas-Menet': menet } });
    } catch (e) {
      return req;
    }
  }

  /**
   *  JOGKÖR-NÉZET: a szűkített jogkör menet-azonosítója a kérés fejlécébe.
   *
   *  Ettől a kiszolgáló is CSAK azzal az egy jogkörrel számol – enélkül a felület
   *  pedagógus menüt mutatna, a háttérben viszont az admin jogosultsága élne, és a
   *  címet beírva minden elérhető maradna.
   *
   *  A saját végpontjait kihagyjuk: az állapot-lekérdezésnek a teljes jogkör-készletet
   *  kell látnia, a visszaváltásnak pedig mindig működnie kell.
   */
  private jogkorNezetFejlec(req: HttpRequest<any>): HttpRequest<any> {
    try {
      /*  CSAK AZ AKTUÁLIS KILÉT NÉZETE. Más nevében belépve az admin saját nézete
       *  (pl. „Adminisztrátor") nem mehet a cél kérései mellé – lásd
       *  jogkor-nezet-tarolo.ts. */
      const n = ervenyesJogkorNezet();
      const menet = n?.menet ? String(n.menet) : '';
      if (!menet || /\/api\/jogkor-nezet(\/|$)/.test(req.url)) return req;
      return req.clone({ setHeaders: { 'X-Jogkor-Nezet': menet } });
    } catch (e) {
      return req;
    }
  }

  /**
   *  Érvényes token, ha van; ha épp készül, rövid ideig megvárjuk; egyébként az,
   *  ami van (akár lejárt) – hogy a viselkedés a legrosszabb esetben se romoljon.
   */
  private async tokentVar(): Promise<string | null> {
    const ervenyes = () => (this.oauth.hasValidAccessToken() ? this.oauth.getAccessToken() : null);

    const most = ervenyes();
    if (most) return most;

    // Készül-e egyáltalán munkamenet? Frissítő jegy vagy egy meglévő (lejárt) token
    // azt jelenti, hogy a könyvtárnak van mit cserélnie. Ha egyik sincs, a felhasználó
    // egyszerűen nincs bejelentkezve – arra nem várunk, azt a beléptető őr kezeli.
    const keszul = !!this.oauth.getRefreshToken() || !!this.oauth.getAccessToken();
    if (!keszul) return this.oauth.getAccessToken() || null;

    //  TARTÓS JEGGYEL (asztali program, mobil) nem várunk tétlenül a lejárt jegyre: a frissítést MI
    //  indítjuk el (ébredés után az időzítő nem fut le, és a kérés a lejárt jeggyel menne ki).
    if (this.oauth.getRefreshToken() && await this.auth.tartosFrissit()) return ervenyes() || this.oauth.getAccessToken() || null;

    if (Date.now() - this.hiabaVart < HIABA_VART_SZUNET_MS) {
      return this.oauth.getAccessToken() || null;
    }

    const hatarido = Date.now() + VARAKOZAS_MS;
    while (Date.now() < hatarido) {
      await new Promise((r) => setTimeout(r, 120));
      const t = ervenyes();
      if (t) return t;
    }

    this.hiabaVart = Date.now();
    return this.oauth.getAccessToken() || null;
  }
}
